import { useCallback, useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import {
  type CurrentUser,
  type VpnClient,
  api,
  formatDate,
} from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { Skeleton } from "@/components/ui/skeleton"
import {
  ArrowUpDown,
  ChevronRight,
  Download,
  FileDown,
  Plus,
  RefreshCw,
  Search,
  Settings2,
  Shield,
  UserPlus,
} from "lucide-react"
import { VpnUserFormSheet } from "@/components/VpnUserFormSheet"
import { VpnPasswordDialog } from "@/components/VpnPasswordDialog"
import { VpnPasswordUnavailableDialog } from "@/components/VpnPasswordUnavailableDialog"
import { getRememberedVpnPassword } from "@/lib/vpn-password-cache"
import {
  VPN_CLIENT_SYNC_INTERVAL_MS,
  syncVpnClients,
} from "../lib/vpn-client-sync"

type Filter = "all" | "active" | "inactive"
type SortOption =
  | "name-asc"
  | "name-desc"
  | "created-desc"
  | "created-asc"
  | "connected-desc"
  | "connected-asc"

const SORT_LABELS: Record<SortOption, string> = {
  "name-asc": "Nome (A-Z)",
  "name-desc": "Nome (Z-A)",
  "created-desc": "Criação (mais recente)",
  "created-asc": "Criação (mais antiga)",
  "connected-desc": "Última conexão (mais recente)",
  "connected-asc": "Última conexão (mais antiga)",
}

type Props = {
  selectedMachine: string
  currentUser: CurrentUser
  onOpenClientDetail: (clientName: string) => void
}
type SyncOptions = { showSuccess?: boolean; showError?: boolean }

export function VpnClientsPage({
  selectedMachine,
  currentUser,
  onOpenClientDetail,
}: Props) {
  const [clients, setClients] = useState<VpnClient[]>([])
  const [loading, setLoading] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const [filter, setFilter] = useState<Filter>("all")
  const [search, setSearch] = useState("")
  const [sort, setSort] = useState<SortOption>("name-asc")
  const [lastSynced, setLastSynced] = useState("")
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const [createOpen, setCreateOpen] = useState(false)
  const [downloadPassword, setDownloadPassword] = useState<{
    username: string
    password: string
  } | null>(null)
  const [unavailablePassword, setUnavailablePassword] = useState<{
    username: string
    action: "download" | "email"
  } | null>(null)

  const canView = currentUser.permissions.some((p) =>
    ["view", "create", "download"].includes(p)
  )
  const canCreate = currentUser.permissions.includes("create")
  const canDownload = currentUser.permissions.includes("download")

  const load = useCallback(async () => {
    if (!selectedMachine || !canView) return
    setLoading(true)
    try {
      const res = await api.getClients(selectedMachine, "all")
      setClients(res.clients)
      setLastSynced(res.lastSyncedAt)
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Erro ao carregar clientes."
      )
    } finally {
      setLoading(false)
    }
  }, [selectedMachine, canView])

  useEffect(() => {
    load()
  }, [load])

  const syncClientList = useCallback(
    async ({ showSuccess = false, showError = false }: SyncOptions = {}) => {
      if (!selectedMachine || !canView) return false
      setRefreshing(true)
      try {
        const res = await syncVpnClients(selectedMachine)
        setClients(res.clients)
        setLastSynced(res.lastSyncedAt)
        if (showSuccess) {
          toast.success("Lista sincronizada com o ambiente remoto.")
        }
        return true
      } catch (err) {
        if (showError) {
          toast.error(
            err instanceof Error ? err.message : "Erro ao sincronizar."
          )
        }
        return false
      } finally {
        setRefreshing(false)
      }
    },
    [selectedMachine, canView]
  )

  useEffect(() => {
    void syncClientList()
  }, [syncClientList])

  useEffect(() => {
    if (!selectedMachine || !canView) return
    const interval = window.setInterval(() => {
      void syncClientList()
    }, VPN_CLIENT_SYNC_INTERVAL_MS)
    return () => window.clearInterval(interval)
  }, [selectedMachine, canView, syncClientList])

  const handleRefresh = async () => {
    await syncClientList({ showSuccess: true, showError: true })
  }

  const handleFilterChange = (nextFilter: Filter) => {
    setFilter(nextFilter)
    void syncClientList()
  }

  const openCreateSheet = () => {
    setCreateOpen(true)
    void syncClientList()
  }

  const handleExportCsv = async () => {
    if (!selectedMachine) return
    const synced = await syncClientList({ showError: true })
    if (!synced) return
    try {
      await api.exportClientsDownload(selectedMachine, filter)
      toast.success("CSV exportado.")
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Falha ao exportar.")
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const result = clients.filter((c) => {
      if (filter !== "all" && c.status !== filter) return false
      if (!q) return true
      return (
        c.name.toLowerCase().includes(q) ||
        (c.fullName ?? "").toLowerCase().includes(q) ||
        (c.email ?? "").toLowerCase().includes(q)
      )
    })

    const dateValue = (value: string) => {
      const time = new Date(value).getTime()
      return Number.isNaN(time) ? 0 : time
    }

    const sorted = [...result]
    sorted.sort((a, b) => {
      switch (sort) {
        case "name-asc":
          return a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" })
        case "name-desc":
          return b.name.localeCompare(a.name, "pt-BR", { sensitivity: "base" })
        case "created-asc":
          return dateValue(a.createdAt) - dateValue(b.createdAt)
        case "created-desc":
          return dateValue(b.createdAt) - dateValue(a.createdAt)
        case "connected-asc":
          return dateValue(a.lastConnectedAt) - dateValue(b.lastConnectedAt)
        case "connected-desc":
          return dateValue(b.lastConnectedAt) - dateValue(a.lastConnectedAt)
        default:
          return 0
      }
    })
    return sorted
  }, [clients, filter, search, sort])

  const allSelected =
    filtered.length > 0 && filtered.every((c) => selected.has(c.name))

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set())
    } else {
      setSelected(new Set(filtered.map((c) => c.name)))
    }
  }

  const toggleOne = (name: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(name) ? next.delete(name) : next.add(name)
      return next
    })
  }

  const handleDownload = async (name: string) => {
    try {
      await api.downloadVpnFile(selectedMachine, name)
      toast.success(`Arquivo ${name}.ovpn baixado.`)
      const rememberedPassword = getRememberedVpnPassword(selectedMachine, name)
      if (rememberedPassword) {
        setDownloadPassword({ username: name, password: rememberedPassword })
      } else {
        setUnavailablePassword({ username: name, action: "download" })
      }
      void syncClientList()
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Erro ao baixar arquivo."
      )
    }
  }

  const handleDownloadSelected = async () => {
    for (const name of selected) {
      await handleDownload(name)
    }
    setSelected(new Set())
    void syncClientList()
  }

  if (!canView) {
    return (
      <div className="flex w-full flex-1 flex-col items-start justify-start p-4 sm:p-6 md:p-8">
        <p className="text-sm text-muted-foreground">
          Sem permissão para acessar esta seção.
        </p>
      </div>
    )
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-6 overflow-hidden p-4 sm:p-6 md:p-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-lg font-semibold tracking-tight">Usuários VPN</h1>
          <p className="text-sm text-muted-foreground">
            {lastSynced
              ? `Última sincronização: ${formatDate(lastSynced)}`
              : "Ainda não sincronizado com o ambiente."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {selected.size > 0 && canDownload && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleDownloadSelected}
            >
              <FileDown />
              Baixar {selected.size} selecionado(s)
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={handleRefresh}
            disabled={refreshing || !selectedMachine}
          >
            <RefreshCw className={refreshing ? "animate-spin" : ""} />
            Sincronizar
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={!selectedMachine}
            onClick={handleExportCsv}
          >
            <Download />
            Exportar CSV
          </Button>
          {canCreate && (
            <Button size="sm" onClick={openCreateSheet}>
              <UserPlus />
              Novo usuário
            </Button>
          )}
        </div>
      </div>

      <Card className="min-h-0 flex-1">
        <CardHeader className="shrink-0 pb-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="text-sm font-medium">
                Clientes VPN
              </CardTitle>
              <CardDescription className="text-xs">
                {loading
                  ? "Carregando..."
                  : `${filtered.length} usuário(s) · ${clients.filter((c) => c.status === "active").length} ativo(s)`}
              </CardDescription>
            </div>
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
              <div className="relative w-full sm:w-56">
                <Search className="absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar usuário..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-7 pl-8 text-sm"
                />
              </div>
              <div className="flex gap-1">
                {(["all", "active", "inactive"] as Filter[]).map((f) => (
                  <Button
                    key={f}
                    size="sm"
                    variant={filter === f ? "default" : "ghost"}
                    className="h-7 px-3 text-xs"
                    onClick={() => handleFilterChange(f)}
                  >
                    {f === "all"
                      ? "Todos"
                      : f === "active"
                        ? "Ativos"
                        : "Inativos"}
                  </Button>
                ))}
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 px-3 text-xs"
                  >
                    <ArrowUpDown className="size-3.5" />
                    Ordenar
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>Ordenar por</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuRadioGroup
                    value={sort}
                    onValueChange={(value) => setSort(value as SortOption)}
                  >
                    {(Object.keys(SORT_LABELS) as SortOption[]).map((option) => (
                      <DropdownMenuRadioItem key={option} value={option}>
                        {SORT_LABELS[option]}
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex min-h-0 flex-1 p-0">
          {loading ? (
            <div className="w-full space-y-2 px-6 pb-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex min-h-48 w-full flex-col items-start gap-3 px-6 py-12 sm:min-h-56">
              <Shield className="size-8 text-muted-foreground/40" />
              <p className="w-full text-left text-sm text-muted-foreground">
                {search.trim()
                  ? "Nenhum usuário VPN encontrado para a busca."
                  : "Nenhum usuário VPN encontrado."}
              </p>
              {canCreate && filter === "all" && !search.trim() && (
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-1"
                  onClick={openCreateSheet}
                >
                  <Plus />
                  Criar usuário VPN
                </Button>
              )}
            </div>
          ) : (
            <div className="min-h-0 flex-1 overflow-auto">
              <table className="w-full min-w-[1040px] border-separate border-spacing-0 text-sm">
                <thead>
                  <tr>
                    <th className="sticky top-0 z-10 w-10 border-b bg-card px-4 py-2 text-left">
                      {canDownload && (
                        <Checkbox
                          checked={allSelected}
                          onCheckedChange={toggleAll}
                          aria-label="Selecionar todos"
                        />
                      )}
                    </th>
                    <th className="sticky top-0 z-10 border-b bg-card px-4 py-2 text-left font-mono text-[11px] font-medium text-muted-foreground">
                      Nome
                    </th>
                    <th className="sticky top-0 z-10 border-b bg-card px-4 py-2 text-left font-mono text-[11px] font-medium text-muted-foreground">
                      Usuário
                    </th>
                    <th className="sticky top-0 z-10 border-b bg-card px-4 py-2 text-left font-mono text-[11px] font-medium text-muted-foreground">
                      E-mail
                    </th>
                    <th className="sticky top-0 z-10 border-b bg-card px-4 py-2 text-left font-mono text-[11px] font-medium text-muted-foreground">
                      Status
                    </th>
                    <th className="sticky top-0 z-10 border-b bg-card px-4 py-2 text-left font-mono text-[11px] font-medium text-muted-foreground">
                      Última conexão
                    </th>
                    <th className="sticky top-0 z-10 border-b bg-card px-4 py-2 text-left font-mono text-[11px] font-medium text-muted-foreground">
                      Criação
                    </th>
                    <th className="sticky top-0 z-10 border-b bg-card px-4 py-2 text-left font-mono text-[11px] font-medium text-muted-foreground">
                      Revogação
                    </th>
                    <th className="sticky top-0 z-10 border-b bg-card px-4 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => (
                    <tr
                      key={c.name}
                      className="cursor-pointer transition-colors last:[&>td]:border-0 hover:bg-muted/30"
                      onClick={() => onOpenClientDetail(c.name)}
                    >
                      <td
                        className="border-b px-4 py-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {canDownload && (
                          <Checkbox
                            checked={selected.has(c.name)}
                            onCheckedChange={() => toggleOne(c.name)}
                            disabled={c.status !== "active"}
                          />
                        )}
                      </td>
                      <td className="border-b px-4 py-3">
                        <span className="text-sm font-medium">
                          {c.fullName || "-"}
                        </span>
                      </td>
                      <td className="border-b px-4 py-3">
                        <span className="font-mono text-xs text-muted-foreground">
                          {c.name}
                        </span>
                      </td>
                      <td className="border-b px-4 py-3 text-xs text-muted-foreground">
                        {c.email || "-"}
                      </td>
                      <td className="border-b px-4 py-3">
                        <Badge
                          variant={
                            c.status === "active" ? "default" : "secondary"
                          }
                          className="font-mono text-[10px]"
                        >
                          {c.status === "active" ? "ativo" : "inativo"}
                        </Badge>
                      </td>
                      <td className="border-b px-4 py-3 text-xs text-muted-foreground">
                        {formatDate(c.lastConnectedAt)}
                      </td>
                      <td className="border-b px-4 py-3 text-xs text-muted-foreground">
                        {formatDate(c.createdAt)}
                      </td>
                      <td className="border-b px-4 py-3 text-xs text-muted-foreground">
                        {c.revokedAt ? formatDate(c.revokedAt) : "-"}
                      </td>
                      <td
                        className="border-b px-4 py-3 text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => onOpenClientDetail(c.name)}
                        >
                          <Settings2 className="size-3.5" />
                          Gerenciar
                          <ChevronRight className="size-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <VpnUserFormSheet
        open={createOpen}
        selectedMachine={selectedMachine}
        onClose={() => setCreateOpen(false)}
        onCreated={async () => {
          await syncClientList({ showError: true })
        }}
      />

      <VpnPasswordDialog
        open={downloadPassword !== null}
        username={downloadPassword?.username ?? ""}
        password={downloadPassword?.password ?? ""}
        onClose={() => setDownloadPassword(null)}
      />
      <VpnPasswordUnavailableDialog
        open={unavailablePassword !== null}
        username={unavailablePassword?.username ?? ""}
        action={unavailablePassword?.action ?? "download"}
        onClose={() => setUnavailablePassword(null)}
      />
    </div>
  )
}
